import apiService from './api.service';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  squad_id?: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    avatar: string | null;
    level: number;
    current_xp: number;
    total_xp: number;
    squad: {
      id: string;
      name: string;
      category: string;
      avatar: string;
    } | null;
  };
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  bio: string | null;
  level: number;
  current_xp: number;
  total_xp: number;
  next_level_xp: number;
  xp_to_next_level: number;
  level_name: string;
  level_icon_url: string;
  streak: number;
  longest_streak: number;
  squad: {
    id: string;
    name: string;
    category: string;
    avatar: string;
  } | null;
  sector: {
    id: string;
    name: string;
    agent_feature_enabled?: boolean;
    seop_feature_enabled?: boolean;
    dashboard_feature_enabled?: boolean;
  } | null;
  role: string;
  auth_provider: 'local' | 'azure_ad';
  needs_profile_completion: boolean;
  tutorial_completed_at: string | null;
  tutorials_seen: Record<string, string | null>;
  is_active: boolean;
  created_at: string;
  // Gate computado pelo backend (OR de role/user.flag/sector.flag).
  // Quando false, AgentRoute bloqueia, Sidebar esconde link, e endpoints
  // retornam 403 `error: agent_feature_not_enabled`.
  agent_feature_enabled: boolean;
  // Flag GLOBAL do Assistente Geral (H.Bot) — kill-switch runtime (G12). Quando
  // false, a Sidebar esconde o link e a rota /assistente mostra "indisponível".
  general_agent_enabled?: boolean;
  // Upload direto de vídeo (browser -> Azure Blob em blocos). Kill-switch de
  // runtime e teto de tamanho vêm do BACKEND: uma VITE_* ficaria congelada
  // dentro da imagem do frontend e não daria para desligar sem rebuild.
  video_direct_upload_enabled?: boolean;
  video_max_size_bytes?: number;
  // Balanceamento de Linha (frente SEOP). Mesmo gate do Hub: OR de
  // role/user.flag/sector.flag, computado pelo backend (B03). Opcional de
  // propósito — os literais de UserProfile espalhados pelos testes não precisam
  // declarar a flag, e o guard trata `undefined` como bloqueado (fail-closed).
  seop_feature_enabled?: boolean;
  // Painéis executivos (/paineis). Computado pelo backend: ADMIN sempre; os
  // demais (CURATOR incluso) só com a flag no usuário ou no setor. Opcional pelo
  // mesmo motivo da flag acima — `undefined` é bloqueado.
  dashboard_feature_enabled?: boolean;
}

export interface MicrosoftLoginResponse extends AuthResponse {
  created: boolean;
}

export interface CompleteProfileRequest {
  sector_id: string;
  squad?: string | null;
  name?: string;
  bio?: string;
}

export interface RegisterMatriculaRequest {
  name: string;
  matricula: string;
  sector_id: string;
  password: string;
  password_confirm: string;
}

export interface RegisterMatriculaResponse {
  detail: string;
  status: 'PENDING';
}

export interface PendingUser {
  id: string;
  name: string;
  matricula: string;
  email: string;
  sector: { id: string; name: string } | null;
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  approval_status_reason: string;
  registration_source: string;
  created_at: string;
}

export interface PendingUsersResponse {
  count: number;
  results: PendingUser[];
}

class AuthService {
  /**
   * Registra um novo usuário
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/register/', data);
    return response.data;
  }

  /**
   * Faz login com email e senha
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/login/', data);
    return response.data;
  }

  /**
   * Atualiza o access token usando o refresh token
   */
  async refreshToken(refreshToken: string): Promise<{ access_token: string }> {
    const response = await apiService.post<{ access_token: string }>('/auth/refresh/', {
      refresh_token: refreshToken,
    });
    return response.data;
  }

  /**
   * Obtém dados do usuário logado
   */
  async getMe(): Promise<UserProfile> {
    const response = await apiService.get<UserProfile>('/auth/me/');
    return response.data;
  }

  /**
   * Faz logout (blacklist do token)
   */
  async logout(refreshToken: string): Promise<void> {
    await apiService.post('/auth/logout/', { refresh_token: refreshToken });
  }

  /**
   * Atualiza dados do perfil
   */
  async updateProfile(data: Partial<Pick<UserProfile, 'name' | 'bio' | 'avatar'>>): Promise<UserProfile> {
    const response = await apiService.patch<UserProfile>('/auth/me/', data);
    return response.data;
  }

  /**
   * Marca um tutorial como concluído.
   * `main` usa o campo legado `tutorial_completed_at`; os contextuais
   * (trails, ranking, ...) usam o dict `tutorials_seen`.
   */
  async completeTutorial(key: string = 'main'): Promise<UserProfile> {
    if (key === 'main') {
      const response = await apiService.patch<UserProfile>('/auth/me/', {
        tutorial_completed_at: 'now',
      });
      return response.data;
    }
    const response = await apiService.patch<UserProfile>('/auth/me/', {
      tutorials_seen: { [key]: 'now' },
    });
    return response.data;
  }

  /**
   * Reinicia um tutorial (será exibido no próximo acesso à área correspondente).
   */
  async resetTutorial(key: string = 'main'): Promise<UserProfile> {
    if (key === 'main') {
      const response = await apiService.patch<UserProfile>('/auth/me/', {
        tutorial_completed_at: null,
      });
      return response.data;
    }
    const response = await apiService.patch<UserProfile>('/auth/me/', {
      tutorials_seen: { [key]: null },
    });
    return response.data;
  }

  /**
   * Login via Microsoft SSO (ID token do MSAL.js)
   */
  async microsoftLogin(idToken: string): Promise<MicrosoftLoginResponse> {
    const response = await apiService.post<MicrosoftLoginResponse>('/auth/microsoft/', {
      id_token: idToken,
    });
    return response.data;
  }

  /**
   * Completar perfil apos registro via SSO
   */
  async completeProfile(data: CompleteProfileRequest): Promise<UserProfile> {
    const response = await apiService.patch<UserProfile>('/auth/complete-profile/', data);
    return response.data;
  }

  /**
   * Cadastro público via matrícula (Frente 1 — US01).
   * Não autentica o usuário: ele fica com status PENDING até admin aprovar.
   */
  async registerMatricula(data: RegisterMatriculaRequest): Promise<RegisterMatriculaResponse> {
    const response = await apiService.post<RegisterMatriculaResponse>('/auth/register-matricula/', data);
    return response.data;
  }

  /**
   * Admin: lista cadastros por matrícula pendentes.
   */
  async getPendingUsers(params?: { search?: string; sector_id?: string }): Promise<PendingUsersResponse> {
    const response = await apiService.get<PendingUsersResponse>('/auth/users/pending/', { params });
    return response.data;
  }

  /**
   * Admin: aprova um cadastro pendente.
   */
  async approvePendingUser(userId: string, reason?: string): Promise<PendingUser> {
    const response = await apiService.post<PendingUser>(`/auth/users/${userId}/approve/`, { reason });
    return response.data;
  }

  /**
   * Admin: rejeita um cadastro pendente (motivo opcional).
   */
  async rejectPendingUser(userId: string, reason?: string): Promise<{ detail: string; id: string }> {
    const response = await apiService.post<{ detail: string; id: string }>(
      `/auth/users/${userId}/reject/`,
      { reason },
    );
    return response.data;
  }
}

export const authService = new AuthService();
